import * as React from 'react'
// libs
import { useDispatch, useSelector } from 'react-redux'
import { clearFields, FieldArray, formValueSelector, reduxForm } from 'redux-form'
// components
import { ReduxEntitySelect, ReduxInput } from 'components/FormElements'
import { Button, BUTTON_LAYOUTS, Scrollbar } from 'components/UI'
// view components
import Fields from './components/Fields'
import renderUsers from 'components/FormElements/UsersList'
import CustomColumns from './components/CustomColumns'
// views
import { AvailableUsersWrapper, Form } from './views'
// constants
import { ENTITIES, Forms, SELECT_GROUP_LABELS } from 'constants/index'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// graphql
import { RELATED_FORMS_SELECT_QUERY, RISK_VIEW_TEMPLATE_SELECT_QUERY } from 'graphql/risk/queries'
// types
import { IOption } from 'components/FormElements/Select'
import { IRiskTemplate } from 'types/graphql'
import { InjectedFormProps } from 'redux-form/lib/reduxForm'
// utils
import map from 'lodash/fp/map'
import pipe from 'lodash/fp/pipe'
import { required, safeGetOr } from 'utils/index'

const formSelector = formValueSelector(Forms.RISK_VIEW_FORM)

// types
type RiskTemplateOption = {
  label: string
  value: IRiskTemplate
}

export type RiskViewFormData = {
  name: string
  riskTemplate: RiskTemplateOption
  relatedForms?: RiskTemplateOption[]
  fields: { active: boolean; fieldId: string; riskTemplateId: string }[]
  customColumns: { id?: string; name: string; sources: { fieldId: IOption; templateId: IOption }[] }[]
  availableToUserOrPositionIds?: IOption[]
}

interface Props {
  isViewUpdate: boolean
  onFormSubmit: () => void
}

const RiskViewForm: React.FC<Props & InjectedFormProps<RiskViewFormData & Record<string, string>>> = ({
  submitting,
  isViewUpdate,
  handleSubmit,
  onFormSubmit
}) => {
  const dispatch = useDispatch()

  const { isRiskTemplate } = React.useContext(RiskTemplateAPIContext)

  const riskTemplate: IRiskTemplate = useSelector((state) => formSelector(state, 'riskTemplate.value'))
  const relatedForms: RiskTemplateOption[] = useSelector((state) => formSelector(state, 'relatedForms'))

  const getTemplatesOptions = pipe(
    safeGetOr<IRiskTemplate, any, IRiskTemplate[]>([], 'data.riskTemplates.data'),
    map<IRiskTemplate, any>((item) => ({ label: item.name, value: item, isDisabled: item.archived }))
  )

  const getRelatedTemplatesOptions = pipe(
    safeGetOr<IRiskTemplate, any, IRiskTemplate[]>([], 'data.relatedRiskTemplates.data'),
    map<IRiskTemplate, any>((item) => ({ label: item.name, value: item, isDisabled: item.archived }))
  )

  const onTemplateChange = () => dispatch(clearFields(Forms.RISK_VIEW_FORM, false, false, 'fields'))

  const onTemplatesSearch = ({ search, refetch }) => {
    refetch({ filter: { name: search } })
  }

  const isOptionSelected = (option, selected = []) => selected.some((item) => item.value.id === option.value.id)

  const prepareUsersOptions = (options: (IOption & { entity: keyof typeof ENTITIES })[] = []) => [
    {
      label: SELECT_GROUP_LABELS.PEOPLE,
      options: options.filter((item) => item.entity === ENTITIES.USER)
    },
    {
      label: SELECT_GROUP_LABELS.POSITIONS,
      options: options.filter((item) => item.entity === ENTITIES.POSITION)
    }
  ]

  return (
    <Form onSubmit={handleSubmit(onFormSubmit)}>
      <Scrollbar>
        {!isRiskTemplate && <ReduxInput label='Name' name='name' validate={required} />}
        <ReduxEntitySelect
          label='Template'
          validate={required}
          name='riskTemplate'
          isDisabled={isViewUpdate || isRiskTemplate}
          handleSearch={onTemplatesSearch}
          getOptions={getTemplatesOptions}
          onSelectChange={onTemplateChange}
          query={RISK_VIEW_TEMPLATE_SELECT_QUERY}
        />
        {!isRiskTemplate && (
          <ReduxEntitySelect
            isMultiple
            skip={!riskTemplate?.id}
            variables={{
              filter: {
                parentRiskTemplateId: riskTemplate?.id
              }
            }}
            name='relatedForms'
            label='Related Forms'
            isDisabled={!riskTemplate}
            handleSearch={onTemplatesSearch}
            getOptions={getRelatedTemplatesOptions}
            query={RELATED_FORMS_SELECT_QUERY}
            isOptionSelected={isOptionSelected}
          />
        )}

        {riskTemplate && <Fields riskTemplate={riskTemplate} />}
        {relatedForms?.map((item) => (
          <Fields key={item.value.id} riskTemplate={item.value} isRelated />
        ))}
        {!isRiskTemplate && <CustomColumns />}
        <AvailableUsersWrapper>
          <FieldArray
            component={renderUsers}
            name='availableToUserOrPositionIds'
            addLabel='user or position'
            prepareOptions={prepareUsersOptions}
          />
        </AvailableUsersWrapper>
      </Scrollbar>
      <Button disabled={submitting} shadowLess type='submit' layout={BUTTON_LAYOUTS.PURPLE}>
        {isViewUpdate ? 'Update' : 'Create'}
      </Button>
    </Form>
  )
}

export default reduxForm<any, any>({
  form: Forms.RISK_VIEW_FORM,
  enableReinitialize: true,
  destroyOnUnmount: true
})(RiskViewForm)
